import React, { useState, useEffect } from "react";
import { BASE_URL } from "../config";
import { getAuthHeaders } from "../services/useAuth";
import Loader from "../dist/Loader";
import CustomButton from "./CustomButton";
import { toast } from "react-toastify";

const StockistList = () => {
  const [states, setStates] = useState([]);
  const [divisions, setDivisions] = useState([]);
  const [modalDivisions, setModalDivisions] = useState([]);
  const [selectedState, setSelectedState] = useState("");
  const [selectedDivision, setSelectedDivision] = useState("");
  const [stockists, setStockists] = useState([]);
  const [selectedStockists, setSelectedStockists] = useState([]);
  const [selectedEmployee, setSelectedEmployee] = useState("");
  const [assignModal, setAssignModal] = useState(false);
  const [filteredEmployees, setFilteredEmployees] = useState([]);
  const [modalSelectedDivision, setModalSelectedDivision] = useState("");
  const [divisionNameMap, setDivisionNameMap] = useState({});
  const [modalSelectedState, setModalSelectedState] = useState("");
  const [recentlyEdited, setRecentlyEdited] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [selectedStatus, setSelectedStatus] = useState("");
  const [showAssignedModal, setShowAssignedModal] = useState(false);
  const [isFilterApplied, setIsFilterApplied] = useState(false);
  const [assignedData, setAssignedData] = useState([]);
  const [assignedModalStockistCode, setAssignedModalStockistCode] = useState(null);
  const [isUnassigning, setIsUnassigning] = useState(false);

  useEffect(() => {
    fetch(`${BASE_URL}/api/states`, { headers: getAuthHeaders() })
      .then((res) => res.json())
      .then((data) => setStates(Array.isArray(data) ? data : []))
      .catch((error) => console.error("Error fetching states:", error));

    fetch(`${BASE_URL}/api/divisions`, { headers: getAuthHeaders() })
      .then((res) => res.json())
      .then((data) => {
        const divs = Array.isArray(data) ? data : [];
        setDivisions(divs);
        const nameMap = {};
        divs.forEach((div) => {
          nameMap[div.id] = div.name;
        });
        setDivisionNameMap(nameMap);
      })
      .catch((error) => console.error("Error fetching divisions:", error));
  }, []);

  useEffect(() => {
    if (!selectedState) {
      fetch(`${BASE_URL}/api/divisions`, { headers: getAuthHeaders() })
        .then((res) => res.json())
        .then((data) => {
          const divs = Array.isArray(data) ? data : [];
          setDivisions(divs);
          const nameMap = {};
          divs.forEach((div) => {
            nameMap[div.id] = div.name;
          });
          setDivisionNameMap(nameMap);
        })
        .catch((err) => console.error("Error fetching divisions:", err));
      setSelectedDivision("");
      return;
    }

    fetch(`${BASE_URL}/api/divisions?state_id=${selectedState}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((data) => {
        const divs = Array.isArray(data) ? data : [];
        setDivisions(divs);
        const nameMap = {};
        divs.forEach((div) => {
          nameMap[div.id] = div.name;
        });
        setDivisionNameMap(nameMap);
        setSelectedDivision("");
      })
      .catch((err) => console.error("Error fetching divisions for state:", err));
  }, [selectedState]);

  const fetchFilteredStockists = () => {
    setIsLoading(true);

    const params = new URLSearchParams();
    if (selectedState) params.append("state_id", selectedState);
    if (selectedDivision) params.append("division_id", selectedDivision);
    if (selectedStatus) {
      params.append("is_active", selectedStatus === "active" ? "true" : "false");
    }
    params.append("purpose", "All");

    fetch(`${BASE_URL}/api/stockist/list?${params.toString()}`, {
      headers: getAuthHeaders(),
    })
      .then((res) => res.json())
      .then((data) => {
        setStockists(Array.isArray(data) ? data : []);
        setIsLoading(false);
      })
      .catch((error) => {
        console.error("Error fetching filtered stockists:", error);
        setIsLoading(false);
        setStockists([]);
      });
  };

  const handleFilterChange = () => {
    if (!selectedState) {
      toast.warning("Please select a State");
      return;
    }
    if (!selectedDivision) {
      toast.warning("Please select a Division");
      return;
    }
    fetchFilteredStockists();
    setIsFilterApplied(true);
  };

  const handleActionClick = (stockist_code) => {
    if (!stockist_code) {
      toast.error("Stockist ID is missing!");
      return;
    }
    window.location.href = `/editstockist/${stockist_code}`;
  };

  const handleAssignCheckbox = (stockist_code) => {
    setSelectedStockists((prev) =>
      prev.includes(stockist_code)
        ? prev.filter((code) => code !== stockist_code)
        : [...prev, stockist_code],
    );
  };

  const handleEmployeeSelect = (e) => {
    setSelectedEmployee(e.target.value);
  };

  const openAssignModal = () => {
    if (selectedStockists.length === 0) {
      toast.error("Please select at least one stockist");
      return;
    }
    setAssignModal(true);
  };

  const closeModal = () => {
    setAssignModal(false);
    setSelectedStockists([]);
    setSelectedEmployee("");
    setModalSelectedDivision("");
    setModalSelectedState("");
    setFilteredEmployees([]);
    setModalDivisions([]);
  };

  const handleAssign = async () => {
    if (!selectedEmployee || selectedStockists.length === 0) {
      toast.error("Please select both employee and at least one stockist");
      return;
    }

    try {
      const response = await fetch(`${BASE_URL}/api/stockist/assign`, {
        method: "POST",
        headers: {
          ...getAuthHeaders(),
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          employee_id: parseInt(selectedEmployee, 10),
          stockist_codes: selectedStockists.map((code) => {
            const parsed = parseInt(code, 10);
            return isNaN(parsed) ? code : parsed;
          }),
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({}));
        throw new Error(errorData.detail || "Assignment failed");
      }

      const result = await response
        .json()
        .catch(() => ({ message: "Assigned successfully" }));

      toast.success(result.message || "Assigned successfully");

      const codesToHighlight = [...selectedStockists];
      setRecentlyEdited(codesToHighlight);
      setTimeout(() => setRecentlyEdited([]), 3000);

      setSelectedStockists([]);
      setSelectedEmployee("");
      setAssignModal(false);
    } catch (error) {
      console.error("Assignment failed:", error);
      toast.error(`Assignment failed: ${error.message}`);
    }
  };

  const unassignStockist = async (employeeId, stockistCode) => {
    const response = await fetch(`${BASE_URL}/api/stockist/unassign`, {
      method: "POST",
      headers: { ...getAuthHeaders(), "Content-Type": "application/json" },
      body: JSON.stringify({
        employee_id: employeeId,
        stockist_codes: [stockistCode],
      }),
    });

    const data = await response.json().catch(() => ({}));
    if (!response.ok) {
      throw new Error(data?.message || "Failed to remove assignment");
    }
  };

  const handleStatusToggle = async (stockist_code, currentStatus) => {
    const newStatus = !currentStatus;
    setIsLoading(true);

    try {
      const response = await fetch(
        `${BASE_URL}/api/stockist/stockists/${stockist_code}/status?is_active=${newStatus}`,
        { method: "PUT", headers: getAuthHeaders() },
      );

      const result = await response.json().catch(() => ({}));

      if (result?.success === false && Array.isArray(result.assigned_to)) {
        setAssignedData(result.assigned_to);
        setAssignedModalStockistCode(stockist_code);
        setShowAssignedModal(true);
        return;
      }

      if (!response.ok) {
        throw new Error(result?.message || "Status update failed");
      }

      setStockists((prev) =>
        prev
          .map((s) =>
            s.stockist_code === stockist_code ? { ...s, is_active: newStatus } : s,
          )
          .filter((s) => {
            if (!selectedStatus) return true;
            return selectedStatus === "active" ? s.is_active : !s.is_active;
          }),
      );
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to update status");
    } finally {
      setIsLoading(false);
    }
  };

  const handleRemoveAssignedRow = async (row) => {
    if (!row?.employee_id) return;

    try {
      setIsUnassigning(true);
      await unassignStockist(row.employee_id, row.stockist_code);

      setAssignedData((prev) => {
        const next = prev.filter(
          (s) =>
            s.employee_id !== row.employee_id ||
            s.stockist_code !== row.stockist_code,
        );
        if (next.length === 0) {
          setShowAssignedModal(false);
        }
        return next;
      });
    } catch (err) {
      console.error(err);
      toast.error(err.message || "Failed to unassign");
    } finally {
      setIsUnassigning(false);
    }
  };

  const closeAssignedModal = () => {
    setShowAssignedModal(false);
    setAssignedData([]);
  };

  return (
    <div className="p-2 border-2 border-gray-300 rounded-lg bg-white">
      {/* Assigned stockist modal */}
      {showAssignedModal && (
        <div className="fixed inset-0 bg-black/50 flex justify-center items-center z-[999]">
          <div className="bg-white w-11/12 max-w-3xl rounded-lg shadow-xl p-6">
            <div className="flex justify-between items-center mb-3">
              <h2 className="text-lg font-semibold text-red-600">
                Stockist {assignedModalStockistCode} Assigned — Remove to Continue
              </h2>
              <button className="text-xl font-bold" onClick={closeAssignedModal}>
                ×
              </button>
            </div>

            <table className="w-full border text-sm">
              <thead className="bg-blue-500 text-white">
                <tr>
                  <th className="border px-2 py-1">S.No</th>
                  <th className="border px-2 py-1">Employee</th>
                  <th className="border px-2 py-1">State</th>
                  <th className="border px-2 py-1">City</th>
                  <th className="border px-2 py-1">Action</th>
                </tr>
              </thead>
              <tbody>
                {assignedData.length === 0 ? (
                  <tr>
                    <td colSpan={5} className="border px-2 py-3 text-center text-gray-500">
                      No assigned employees found.
                    </td>
                  </tr>
                ) : (
                  assignedData.map((row, i) => (
                    <tr key={i}>
                      <td className="border px-2 py-1 text-center">{i + 1}</td>
                      <td className="border px-2 py-1">{row.employee_name}</td>
                      <td className="border px-2 py-1">{row.state}</td>
                      <td className="border px-2 py-1">{row.city}</td>
                      <td className="border px-2 py-1 text-center">
                        <button
                          onClick={() => handleRemoveAssignedRow(row)}
                          className="bg-red-600 text-white px-3 py-1 rounded text-xs"
                          disabled={isUnassigning}
                        >
                          {isUnassigning ? "Removing..." : "Remove"}
                        </button>
                      </td>
                    </tr>
                  ))
                )}
              </tbody>
            </table>

            <div className="text-right mt-3">
              <button
                onClick={closeAssignedModal}
                className="px-4 py-2 bg-gray-500 text-white rounded"
              >
                Close
              </button>
            </div>
          </div>
        </div>
      )}

      <div className="p-2 border-2 border-gray-300 rounded-lg bg-white">
        {isLoading && (
          <div className="fixed inset-0 z-[9999] flex items-center justify-center bg-black/30 backdrop-blur-sm">
            <Loader onCancel={() => setIsLoading(false)} />
          </div>
        )}

        <main className={`${isLoading ? "pointer-events-none opacity-60" : ""} flex-1`}>
          <div className="p-4 border-2 border-gray-300 rounded-lg bg-gray-50">
            <div className="max-w-[99%] mx-auto py-6">
              <div className="text-center mb-6">
                <h1 className="text-2xl sm:text-3xl font-bold text-gray-800">
                  <span className="text-blue-700">Stockist </span>
                  <span className="text-blue-500">List</span>
                </h1>
              </div>

              {/* Filters card */}
              <div className="mx-auto max-w-3xl bg-gray-50 border border-gray-200 rounded-lg p-6 shadow-sm mb-6">
                <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      State
                    </label>
                    <select
                      value={selectedState}
                      onChange={(e) => {
                        setSelectedState(e.target.value);
                        setSelectedDivision("");
                        setIsFilterApplied(false);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select State</option>
                      {states.map((state) => (
                        <option key={state.id} value={state.id}>
                          {state.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Division
                    </label>
                    <select
                      value={selectedDivision}
                      onChange={(e) => {
                        setSelectedDivision(e.target.value);
                        setIsFilterApplied(false);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md"
                    >
                      <option value="">Select Division</option>
                      {divisions.map((div) => (
                        <option key={div.id} value={div.id}>
                          {div.name}
                        </option>
                      ))}
                    </select>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-1">
                      Status
                    </label>
                    <select
                      value={selectedStatus}
                      onChange={(e) => {
                        setSelectedStatus(e.target.value);
                        setIsFilterApplied(false);
                      }}
                      className="w-full px-3 py-2 border border-gray-300 rounded-md text-sm focus:outline-none focus:ring-2 focus:ring-blue-500"
                    >
                      <option value="">All Status</option>
                      <option value="active">Active</option>
                      <option value="inactive">Inactive</option>
                    </select>
                  </div>
                </div>
              </div>

              {/* Buttons */}
              <div className="flex justify-center items-center gap-4 mb-6">
                <CustomButton
                  variant="go"
                  onClick={handleFilterChange}
                  disabled={isLoading}
                >
                  {isLoading ? "Loading..." : "GO"}
                </CustomButton>

                {selectedStockists.length > 0 && (
                  <CustomButton
                    className="bg-blue-600 hover:bg-blue-700 text-white px-4 py-2 rounded-md shadow-sm flex items-center justify-center whitespace-nowrap"
                    onClick={openAssignModal}
                  >
                    <span className="text-center">
                      Assign ({selectedStockists.length})
                    </span>
                  </CustomButton>
                )}
              </div>

              {/* Assign modal */}
              {assignModal && (
                <div className="fixed inset-0 bg-black/50 flex items-center justify-center z-50">
                  <div className="bg-white rounded-lg shadow-lg w-11/12 max-w-3xl p-6 max-h-[90vh] overflow-y-auto">
                    <div className="flex items-center justify-between mb-4">
                      <h3 className="text-lg font-semibold">
                        Assign Employee to {selectedStockists.length} Stockist(s)
                      </h3>
                      <CustomButton
                        onClick={closeModal}
                        className="text-gray-600 hover:text-gray-800 text-xl font-bold"
                      >
                        &times;
                      </CustomButton>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-3 gap-4 mb-4">
                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select State
                        </label>
                        <select
                          value={modalSelectedState}
                          onChange={(e) => {
                            const stateId = e.target.value;
                            setModalSelectedState(stateId);
                            setModalSelectedDivision("");
                            setSelectedEmployee("");
                            setFilteredEmployees([]);
                            setModalDivisions([]);

                            if (stateId) {
                              fetch(
                                `${BASE_URL}/api/divisions?state_id=${stateId}`,
                                { headers: getAuthHeaders() },
                              )
                                .then((res) => res.json())
                                .then((data) =>
                                  setModalDivisions(Array.isArray(data) ? data : []),
                                )
                                .catch((err) =>
                                  console.error("Error fetching divisions for modal:", err),
                                );
                            }
                          }}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md"
                        >
                          <option value="">Select State</option>
                          {states.map((state) => (
                            <option key={state.id} value={state.id}>
                              {state.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select Division
                        </label>
                        <select
                          value={modalSelectedDivision}
                          onChange={(e) => {
                            const divisionId = e.target.value;
                            setModalSelectedDivision(divisionId);
                            setSelectedEmployee("");

                            if (divisionId && modalSelectedState) {
                              const selectedDivisionObj = modalDivisions.find(
                                (d) => String(d.id) === String(divisionId),
                              );
                              const divisionName = selectedDivisionObj?.name;

                              if (divisionName) {
                                fetch(
                                  `${BASE_URL}/api/employees?state_id=${modalSelectedState}&division=${encodeURIComponent(divisionName)}`,
                                  { headers: getAuthHeaders() },
                                )
                                  .then((res) => res.json())
                                  .then((data) =>
                                    setFilteredEmployees(Array.isArray(data) ? data : []),
                                  )
                                  .catch((err) =>
                                    console.error("Error fetching employees for modal division:", err),
                                  );
                              } else {
                                setFilteredEmployees([]);
                              }
                            } else {
                              setFilteredEmployees([]);
                            }
                          }}
                          disabled={!modalSelectedState}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:opacity-60"
                        >
                          <option value="">Select Division</option>
                          {modalDivisions.map((div) => (
                            <option key={div.id} value={div.id}>
                              {div.name}
                            </option>
                          ))}
                        </select>
                      </div>

                      <div>
                        <label className="block text-sm font-medium text-gray-700 mb-1">
                          Select Employee
                        </label>
                        <select
                          value={selectedEmployee}
                          onChange={handleEmployeeSelect}
                          disabled={!modalSelectedDivision || filteredEmployees.length === 0}
                          className="w-full px-3 py-2 border border-gray-300 rounded-md disabled:opacity-60"
                        >
                          <option value="">
                            {filteredEmployees.length === 0
                              ? modalSelectedDivision
                                ? "No employees in this division"
                                : "Select a division first"
                              : "Select Employee"}
                          </option>
                          {filteredEmployees.map((emp) => (
                            <option key={emp.id} value={emp.id}>
                              {emp.first_name} {emp.last_name}  | {emp.employee} ({emp.designation} | {emp.division} | {emp.headquarter_name} | {emp.state_name})
                            </option>
                          ))}
                        </select>
                      </div>
                    </div>

                    <div className="flex justify-end gap-3">
                      <CustomButton
                        className="bg-green-600 hover:bg-green-700 text-white px-4 py-2 rounded"
                        onClick={handleAssign}
                      >
                        Assign
                      </CustomButton>
                      <CustomButton
                        className="bg-red-600 hover:bg-red-700 text-white px-4 py-2 rounded"
                        onClick={closeModal}
                      >
                        Cancel
                      </CustomButton>
                    </div>
                  </div>
                </div>
              )}

              {/* Table */}
              {isFilterApplied && (
                <div className="border rounded shadow table-wrapper mt-4">
                  {stockists.length === 0 ? (
                    <div className="flex items-center justify-center py-10 text-gray-500 text-sm">
                      No stockists found for the selected filters.
                    </div>
                  ) : (
                    <table className="unlock-dcr-leave-table text-sm">
                      <thead className="bg-blue-500 text-white">
                        <tr>
                          <th className="border px-3 py-2 sticky top-0 bg-blue-500 z-50">Assign Stockist</th>
                          <th className="border px-3 py-2 sticky top-0 bg-blue-500 z-50">S.No</th>
                          <th className="border px-3 py-2 sticky top-0 bg-blue-500 z-50">Stockist Code</th>
                          <th className="border px-3 py-2 sticky top-0 bg-blue-500 z-50">Division Name</th>
                          <th className="border px-3 py-2 sticky top-0 bg-blue-500 z-50">Stockist</th>
                          <th className="border px-3 py-2 sticky top-0 bg-blue-500 z-50">Contact Person</th>
                          <th className="border px-3 py-2 sticky top-0 bg-blue-500 z-50">State</th>
                          <th className="border px-3 py-2 sticky top-0 bg-blue-500 z-50">City</th>
                          <th className="border px-3 py-2 sticky top-0 bg-blue-500 z-50">Phone</th>
                          <th className="border px-3 py-2 sticky top-0 bg-blue-500 z-50">GSTIN</th>
                          <th className="border px-3 py-2 sticky top-0 bg-blue-500 z-50">Address</th>
                          <th className="border px-3 py-2 sticky top-0 bg-blue-500 z-50">Status</th>
                          <th className="border px-3 py-2 sticky top-0 bg-blue-500 z-50">Active/Inactive</th>
                          <th className="border px-3 py-2 sticky top-0 bg-blue-500 z-50">Actions</th>
                        </tr>
                      </thead>
                      <tbody>
                        {stockists.map((stockist, index) => (
                          <tr
                            key={stockist.stockist_code ?? index}
                            className={
                              recentlyEdited.includes(stockist.stockist_code)
                                ? "bg-yellow-100"
                                : ""
                            }
                          >
                            <td className="border px-3 py-3 text-center">
                              <input
                                type="checkbox"
                                checked={selectedStockists.includes(stockist.stockist_code)}
                                onChange={() => handleAssignCheckbox(stockist.stockist_code)}
                                className="scale-110 cursor-pointer"
                              />
                            </td>
                            <td className="border px-3 py-3 text-center">{index + 1}</td>
                            <td className="border px-3 py-3 text-center">{stockist.stockist_code}</td>
                            <td className="border px-3 py-3">{stockist.division_name || "N/A"}</td>
                            <td className="border px-3 py-3">{stockist.stockist_name}</td>
                            <td className="border px-3 py-3">{stockist.contact_person}</td>
                            <td className="border px-3 py-3 text-center">{stockist.state_name || "N/A"}</td>
                            <td className="border px-3 py-3 text-center">{stockist.city_name || "N/A"}</td>
                            <td className="border px-3 py-3 text-center">{stockist.mobile}</td>
                            <td className="border px-3 py-3 text-center">{stockist.gstin}</td>
                            <td className="border px-3 py-3">{stockist.address}</td>
                            <td className="border px-3 py-3 text-center">
                              <span
                                className={`inline-block px-3 py-1 rounded-full text-xs ${
                                  stockist.is_active
                                    ? "bg-green-100 text-green-700"
                                    : "bg-gray-100 text-gray-700"
                                }`}
                              >
                                {stockist.is_active ? "Active" : "Inactive"}
                              </span>
                            </td>
                            <td className="border px-3 py-3 text-center">
                              <label className="inline-flex items-center cursor-pointer">
                                <input
                                  type="checkbox"
                                  className="sr-only peer"
                                  checked={stockist.is_active || false}
                                  onChange={() =>
                                    handleStatusToggle(stockist.stockist_code, stockist.is_active)
                                  }
                                />
                                <div className="relative w-11 h-6 bg-gray-200 rounded-full peer-checked:bg-green-600">
                                  <div
                                    className={`absolute top-0.5 left-1 bg-white rounded-full h-5 w-5 shadow transform transition-transform ${
                                      stockist.is_active ? "translate-x-[1.25rem]" : ""
                                    }`}
                                  />
                                </div>
                              </label>
                            </td>
                            <td className="border px-3 py-3 text-center">
                              <button
                                className="bg-blue-600 hover:bg-blue-700 text-white px-3 py-1 rounded"
                                onClick={() => handleActionClick(stockist.stockist_code)}
                              >
                                View/Edit
                              </button>
                            </td>
                          </tr>
                        ))}
                      </tbody>
                    </table>
                  )}
                </div>
              )}
            </div>
          </div>
        </main>
      </div>
    </div>
  );
};

export default StockistList;